import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, GraduationCap, BookOpen, School, Briefcase, CheckCircle2 } from "lucide-react";
import { PageHero } from "@/components/common/SectionHeading";
import SEO from "@/components/common/SEO";
import { fetchDepartments, fetchPrograms } from "@/lib/lnlApi";

// Basic education + SHS strands + TVL are published content, not database rows.
// They come straight from the official LNL list and never change via the CMS.
const BASIC_EDUCATION = [
    { label: "Elementary", grades: "Grades 1 – 6" },
    { label: "Junior High School", grades: "Grades 7 – 10" },
    { label: "Senior High School", grades: "Grades 11 – 12" },
];

const SHS_STRANDS = [
    { code: "ABM", name: "Accountancy, Business and Management" },
    { code: "STEM", name: "Science, Technology, Engineering and Mathematics" },
    { code: "GAS", name: "General Academic Strand" },
    { code: "HUMSS", name: "Humanities and Social Sciences" },
];

const TVL_OFFERS = ["ICT", "Cookery", "Caregiving"];

export default function Academics() {
    const [depts, setDepts] = useState([]);
    const [programs, setPrograms] = useState([]);

    useEffect(() => {
        fetchDepartments().then(setDepts);
        fetchPrograms().then((rows) => {
            // Keep an explicit, canonical order even if the API returns rows
            // without `order` (older seeds).
            const sorted = [...(rows || [])].sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
            setPrograms(sorted);
        });
    }, []);

    return (
        <div data-testid="academics-page">
            <SEO
                title="Courses Offered — College, Basic Education, SHS Strands & TVL"
                description="The official Lyceum Northern Luzon list of courses — college degrees (Nursing, Pharmacy, Accountancy, Business Administration, Criminology, IT, Secondary & Elementary Education, Hospitality, Tourism), basic education, Senior High School strands (ABM, STEM, GAS, HUMSS) and TVL offers (ICT, Cookery, Caregiving)."
                path="/academics"
            />
            <PageHero
                eyebrow="Courses Offered"
                title="College programs, basic education, SHS strands and TVL"
                description="The official LNL course list — career-focused college degrees, complete basic education and TVL tracks, all taught on one campus in Urdaneta City."
                breadcrumbs={[{ label: "Home", to: "/" }, { label: "Courses Offered" }]}
            />

            {/* ============ COLLEGE PROGRAMS ============ */}
            <section className="py-14 bg-white" data-testid="section-college-programs">
                <div className="lnl-container">
                    <SectionTitle
                        icon={GraduationCap}
                        eyebrow="College"
                        title="College Programs"
                        description="Ten bachelor's degree programs across seven colleges. Programs with majors show the available specializations underneath."
                    />
                    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="college-programs-grid">
                        {programs.map((p, idx) => (
                            <article
                                key={p.slug}
                                className="lnl-card p-5 flex flex-col"
                                data-testid={`program-card-${p.slug}`}
                            >
                                <div className="flex items-start gap-3">
                                    <div className="w-9 h-9 rounded-full bg-lnl-green-bg text-lnl-green-deep grid place-items-center shrink-0 font-heading font-bold text-sm">
                                        {idx + 1}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="lnl-eyebrow truncate">
                                            {(depts.find((d) => d.slug === p.department_slug) || {}).name || "College"}
                                        </div>
                                        <h3 className="font-heading font-bold text-lnl-charcoal text-[15px] leading-snug mt-0.5">
                                            {p.name}
                                        </h3>
                                    </div>
                                </div>

                                {Array.isArray(p.majors) && p.majors.length > 0 && (
                                    <div
                                        className="mt-3 rounded-md border border-lnl-gold-soft/60 bg-lnl-gold-soft/10 p-3"
                                        data-testid={`program-majors-${p.slug}`}
                                    >
                                        <div className="text-[10px] uppercase tracking-[0.16em] font-heading font-bold text-lnl-green-deep">
                                            Majors
                                        </div>
                                        <ul className="mt-1.5 space-y-1">
                                            {p.majors.map((m) => (
                                                <li
                                                    key={m}
                                                    className="text-xs text-lnl-charcoal flex items-center gap-1.5"
                                                    data-testid={`program-major-${p.slug}-${m.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                                >
                                                    <CheckCircle2 className="h-3.5 w-3.5 text-lnl-green shrink-0" />
                                                    {m}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <div className="mt-auto pt-4">
                                    <Link
                                        to={`/academics/${p.department_slug}`}
                                        className="text-xs font-heading font-bold text-lnl-green-deep hover:text-lnl-green inline-flex items-center gap-1"
                                    >
                                        See college <ArrowRight className="h-3.5 w-3.5" />
                                    </Link>
                                </div>
                            </article>
                        ))}
                        {programs.length === 0 && (
                            <div className="col-span-full text-sm text-lnl-gray py-6 text-center">Loading courses…</div>
                        )}
                    </div>
                </div>
            </section>

            {/* ============ BASIC EDUCATION ============ */}
            <section className="py-14 bg-lnl-off-white" data-testid="section-basic-education">
                <div className="lnl-container">
                    <SectionTitle
                        icon={BookOpen}
                        eyebrow="Department of Basic Education"
                        title="Basic Education Courses"
                        description="A continuous K-to-12 pathway, taught on the same campus as our college programs."
                    />
                    <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="basic-education-grid">
                        {BASIC_EDUCATION.map((be) => (
                            <div key={be.label} className="lnl-card p-5" data-testid={`basic-ed-${be.label.toLowerCase().replace(/\s+/g, "-")}`}>
                                <div className="w-10 h-10 rounded-full bg-lnl-green text-white grid place-items-center">
                                    <School className="h-5 w-5" />
                                </div>
                                <h3 className="font-heading font-bold text-lnl-charcoal text-lg mt-3">{be.label}</h3>
                                <div className="text-sm text-lnl-gray mt-1">{be.grades}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ SENIOR HIGH SCHOOL STRANDS ============ */}
            <section className="py-14 bg-white" data-testid="section-shs-strands">
                <div className="lnl-container">
                    <SectionTitle
                        icon={GraduationCap}
                        eyebrow="Senior High School · Grades 11 – 12"
                        title="Senior High School Strands"
                        description="Academic strands that prepare learners for college admission and future careers."
                    />
                    <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="shs-strands-grid">
                        {SHS_STRANDS.map((s) => (
                            <div
                                key={s.code}
                                className="lnl-card p-5 text-center"
                                data-testid={`shs-strand-${s.code.toLowerCase()}`}
                            >
                                <div className="font-heading font-bold text-2xl text-lnl-green-deep">{s.code}</div>
                                <div className="text-xs text-lnl-gray mt-2 leading-relaxed">{s.name}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ TVL OFFERS ============ */}
            <section className="py-14 bg-lnl-off-white" data-testid="section-tvl">
                <div className="lnl-container">
                    <SectionTitle
                        icon={Briefcase}
                        eyebrow="Technical-Vocational-Livelihood"
                        title="TVL Offers"
                        description="Hands-on tracks aligned with industry and TESDA competencies."
                    />
                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="tvl-grid">
                        {TVL_OFFERS.map((t) => (
                            <div
                                key={t}
                                className="lnl-card p-5 flex items-center gap-3"
                                data-testid={`tvl-${t.toLowerCase()}`}
                            >
                                <div className="w-9 h-9 rounded-full bg-lnl-gold-soft/40 text-lnl-green-deep grid place-items-center shrink-0">
                                    <Briefcase className="h-4 w-4" />
                                </div>
                                <div className="font-heading font-bold text-lnl-charcoal">{t}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ DEPARTMENTS DIRECTORY (unchanged) ============ */}
            <section className="py-14 bg-white" data-testid="section-departments">
                <div className="lnl-container">
                    <SectionTitle
                        icon={School}
                        eyebrow="Colleges & Departments"
                        title="Our academic units"
                        description="Visit each college for its department profile, programs and leadership."
                    />
                    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {depts.map((d) => (
                            <Link
                                key={d.slug}
                                to={`/academics/${d.slug}`}
                                className="lnl-card p-5 hover:border-lnl-green/40 transition-colors"
                                data-testid={`dept-card-${d.slug}`}
                            >
                                <div className="lnl-eyebrow">{d.level === "college" ? "College" : "Basic education"}</div>
                                <div className="font-heading font-bold text-lg text-lnl-charcoal mt-1">{d.name}</div>
                                <div className="text-sm text-lnl-gray mt-2">{d.short}</div>
                                <div className="mt-4 text-xs text-lnl-green-deep font-heading font-semibold inline-flex items-center gap-1">
                                    View department <ArrowRight className="h-3.5 w-3.5" />
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}

function SectionTitle({ icon: Icon, eyebrow, title, description }) {
    return (
        <div className="flex items-start gap-4">
            {Icon && (
                <div className="w-11 h-11 rounded-full bg-lnl-green-bg text-lnl-green-deep grid place-items-center shrink-0">
                    <Icon className="h-5 w-5" />
                </div>
            )}
            <div>
                <div className="lnl-eyebrow">{eyebrow}</div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-lnl-charcoal mt-1">{title}</h2>
                {description && <p className="text-sm text-lnl-gray mt-2 max-w-2xl">{description}</p>}
            </div>
        </div>
    );
}
